import { CollateralAsset, DebtAsset } from './types';
import {
  normalizeArgs as _normalizeArgs,
  normalizeAsset,
  normalizeCollateralAsset,
  normalizePercentage,
} from './normalizeArgs';
import { getCropTokens } from './cropLTV';
import { performCalculations as _performCalculations } from './performCalculations';
import { flow, pipe } from 'fp-ts/lib/function';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { mapToCollateralAsset, mapToDebtAsset } from './calculateLTV';
import { BigMath, roundToSignificantDigits } from '@silo/utils';
import { PRECISION_FACTOR } from '@silo/types';

interface ReachHealthFactorParams {
  collateralAssets: CollateralAsset[];
  debtAssets: DebtAsset[];
  healthFactor: bigint;
  asset: SiloAsset;
}

export interface Instructions {
  action: 'deposit' | 'borrow' | 'withdraw' | 'repay';
  tokens: bigint;
  decimals: number;
  asset: SiloAsset;
}

type ReachHealthFactor = (params: ReachHealthFactorParams) => Instructions;

const performCalculations: ReachHealthFactor = ({ collateralAssets, debtAssets, healthFactor, asset }) => {
  const {
    liquidationThresholdValue,
    borrowedValue,
    healthFactor: currentHealthFactor,
  } = _performCalculations({ collateralAssets, debtAssets });

  const riskFactor = PRECISION_FACTOR - healthFactor;

  const preparedAsset = prepareAsset({
    asset,
    collateralAssets,
    debtAssets,
    currentHealthFactor,
    newHealthFactor: healthFactor,
  });

  if (isCollateralAsset(preparedAsset)) {
    const { liquidationThreshold, price, decimals } = preparedAsset;

    if (riskFactor === 0n) {
      return {
        action: 'deposit',
        tokens: 0n,
        decimals,
        asset,
      };
    }

    const targetLiquidationValue = (borrowedValue * PRECISION_FACTOR) / riskFactor;
    const liquidationValueAdjustment = targetLiquidationValue - liquidationThresholdValue;
    const collateralValueAdjustment = (liquidationValueAdjustment * PRECISION_FACTOR) / liquidationThreshold;
    const collateralTokensAdjustment = (collateralValueAdjustment * PRECISION_FACTOR) / price;

    return {
      action: collateralTokensAdjustment >= 0n ? 'deposit' : 'withdraw',
      tokens: BigMath.abs(collateralTokensAdjustment),
      decimals,
      asset,
    };
  } else {
    // debt asset
    const { price, decimals } = preparedAsset;
    const borrowedTokensAdjustment = debtAssetBorrowedTokensAdjustment({
      liquidationThresholdValue,
      riskFactor,
      borrowedValue,
      price,
    });

    return {
      action: borrowedTokensAdjustment >= 0n ? 'borrow' : 'repay',
      tokens: BigMath.abs(borrowedTokensAdjustment),
      decimals,
      asset,
    };
  }
};

export const debtAssetBorrowedTokensAdjustment = ({
  liquidationThresholdValue,
  riskFactor,
  borrowedValue,
  price,
}: {
  liquidationThresholdValue: bigint;
  riskFactor: bigint;
  borrowedValue: bigint;
  price: bigint;
}) => {
  const targetBorrowedValue = (liquidationThresholdValue * riskFactor) / PRECISION_FACTOR;
  const borrowedValueAdjustment = targetBorrowedValue - borrowedValue;
  return (borrowedValueAdjustment * PRECISION_FACTOR) / price;
};

type PrepareAsset = (params: {
  asset: SiloAsset;
  collateralAssets: CollateralAsset[];
  debtAssets: DebtAsset[];
  currentHealthFactor: bigint;
  newHealthFactor: bigint;
}) => CollateralAsset | DebtAsset;

const prepareAsset: PrepareAsset = ({ asset, collateralAssets, debtAssets, currentHealthFactor, newHealthFactor }) => {
  const existingCollateralAsset = collateralAssets.find(({ assetAddress }) => assetAddress === asset.assetAddress);

  if (existingCollateralAsset) return existingCollateralAsset;

  const existingDebtAsset = debtAssets.find(({ assetAddress }) => assetAddress === asset.assetAddress);

  if (existingDebtAsset) return existingDebtAsset;

  // asset was not found in existing loan - figure out if it should be a collateral or debt
  if (newHealthFactor >= currentHealthFactor) {
    // if health factor is improving, the new asset must be a collateral asset
    return pipe(asset, mapToCollateralAsset, normalizeCollateralAsset);
  }

  // if health factor is declining, the new asset must be a debt asset
  return pipe(asset, mapToDebtAsset, normalizeAsset);
};

const isCollateralAsset = (asset: CollateralAsset | DebtAsset): asset is CollateralAsset =>
  'liquidationThreshold' in asset;

const normalizeArgs = ({
  collateralAssets,
  debtAssets,
  healthFactor,
  asset,
}: ReachHealthFactorParams): ReachHealthFactorParams => ({
  ..._normalizeArgs({ collateralAssets, debtAssets }),
  healthFactor: normalizePercentage(healthFactor),
  asset,
});

// TODO: may need to look into how values are rounded (round down for borrow? etc.)
const cropInstructions = ({ tokens, decimals, ...rest }: Instructions): Instructions => {
  const croppedTokens = getCropTokens(decimals)(tokens);
  const roundedTokens = roundToSignificantDigits(croppedTokens, 3);

  return {
    ...rest,
    decimals,
    tokens: roundedTokens,
  };
};

export const reachHealthFactor = flow(normalizeArgs, performCalculations, cropInstructions);
